// The block passes the per-product limit: 240 for 4 x 6 inch greeting cards,
// 120 for mini cards. MAX_... is the hard ceiling this module will ever accept.
const DEFAULT_GREETING_CARD_MESSAGE_LENGTH = 120;
const MAX_GREETING_CARD_MESSAGE_LENGTH = 240;
const MAX_GREETING_CARD_MESSAGES = 25;

export function greetingCardMessageLimit(maxLength) {
  const limit = Number(maxLength);
  if (Number.isFinite(limit) && limit > 0) {
    return Math.min(Math.floor(limit), MAX_GREETING_CARD_MESSAGE_LENGTH);
  }
  return DEFAULT_GREETING_CARD_MESSAGE_LENGTH;
}

export function validateGreetingCardMessage(value, maxLength) {
  const message = String(value ?? '').trim();
  const limit = greetingCardMessageLimit(maxLength);
  if (!message) throw new Error('Please write a greeting card message or turn off the paid message option.');
  if (message.length > limit) {
    throw new Error(`Please keep each greeting card message to ${limit} characters or fewer.`);
  }
  return message;
}

function toQuantity(value) {
  return Math.max(1, Number.parseInt(String(value || 1), 10) || 1);
}

function createGroupId(provided) {
  const clean = String(provided || '').trim();
  if (clean) return clean;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `greeting-${crypto.randomUUID()}`;
  }
  return `greeting-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function isGreetingProperty(key) {
  return key === 'Message for Card' ||
    key === 'Greeting Card Message' ||
    key === 'Greeting Card Message Add-on' ||
    key === 'Greeting Card Message Add-ons' ||
    key === '_Greeting message group' ||
    key === '_Greeting message count' ||
    /^Message \d+ for Card$/.test(key) ||
    /^Greeting Card Message \d+$/.test(key);
}

function withoutGreetingProperties(properties) {
  const clean = { ...(properties || {}) };
  for (const key of Object.keys(clean)) {
    if (isGreetingProperty(key)) delete clean[key];
  }
  return clean;
}

function clearedGreetingProperties(properties) {
  // Shopify's Cart Ajax API ignores an empty `properties: {}` replacement and
  // keeps the old line-item properties. A private marker makes the replacement
  // non-empty so the paid message fields are actually removed before the fee
  // line is removed.
  const clean = withoutGreetingProperties(properties);
  if (!Object.keys(clean).length) clean['_Greeting message removed'] = 'true';
  return clean;
}

function normalizeMessages(input) {
  const messages = Array.isArray(input.messages) ? input.messages : [];
  if (!messages.length) throw new Error('Please add at least one greeting card message.');
  if (messages.length > MAX_GREETING_CARD_MESSAGES) throw new Error('Too many greeting card messages were added.');
  if (messages.length > 1 && input.includeCardChoice !== true) {
    throw new Error('Multiple messages can only be added when the product includes multiple greeting cards.');
  }

  const normalized = messages.map((entry) => ({
    messageForCard: String(entry?.messageForCard || '').trim(),
    message: validateGreetingCardMessage(entry?.message, input.maxLength),
  }));

  if (input.includeCardChoice === true || input.requireCardChoice === true) {
    const seen = new Set();
    for (const entry of normalized) {
      if (!entry.messageForCard) throw new Error('Please choose which greeting card each message is for.');
      const identity = entry.messageForCard.toLocaleLowerCase();
      if (seen.has(identity)) throw new Error('Please choose a different greeting card for each message.');
      seen.add(identity);
    }
  }

  return normalized;
}

/**
 * Build one main product line plus one linked ₹50 fee line. The fee line quantity
 * is message count × product quantity. Customer-visible text lives only on main.
 */
export function buildGreetingCardMessagesPayload(input) {
  const mainVariantId = Number(input.mainVariantId);
  const addonVariantId = Number(input.addonVariantId);
  const quantity = toQuantity(input.quantity);
  const productTitle = String(input.productTitle || 'Greeting card').trim();
  const productHandle = String(input.productHandle || '').trim();
  const includeCardChoice = input.includeCardChoice === true;
  const messages = normalizeMessages({ ...input, includeCardChoice });
  const groupId = createGroupId(input.groupId);
  const mainProperties = withoutGreetingProperties(input.mainProperties);

  if (!Number.isSafeInteger(mainVariantId) || mainVariantId <= 0) {
    throw new Error('Please select a greeting card option first.');
  }
  if (!Number.isSafeInteger(addonVariantId) || addonVariantId <= 0) {
    throw new Error('Greeting card message add-on is not configured.');
  }

  if (messages.length === 1) {
    if (includeCardChoice) mainProperties['Message for Card'] = messages[0].messageForCard;
    mainProperties['Greeting Card Message'] = messages[0].message;
    mainProperties['Greeting Card Message Add-on'] = '₹50';
  } else {
    messages.forEach((entry, index) => {
      const number = index + 1;
      mainProperties[`Message ${number} for Card`] = entry.messageForCard;
      mainProperties[`Greeting Card Message ${number}`] = entry.message;
    });
    mainProperties['Greeting Card Message Add-ons'] = `${messages.length} × ₹50`;
  }

  mainProperties['_Greeting message group'] = groupId;
  mainProperties['_Greeting message count'] = String(messages.length);

  const linkedCardNames = [...new Set(messages
    .map((entry) => entry.messageForCard || productTitle)
    .map((name) => String(name).split('|')[0].trim())
    .filter(Boolean))];
  const publicCardProperty = linkedCardNames.length > 1
    ? { 'For Greeting Cards': linkedCardNames.join(', ') }
    : { 'For Greeting Card': linkedCardNames[0] || productTitle };

  return {
    items: [
      { id: mainVariantId, quantity, properties: mainProperties },
      {
        id: addonVariantId,
        quantity: quantity * messages.length,
        properties: {
          ...publicCardProperty,
          '_Greeting card message add-on': 'true',
          '_Greeting message group': groupId,
          '_Greeting message count': String(messages.length),
          '_Linked greeting card variant': String(mainVariantId),
          '_Linked greeting card handle': productHandle,
          '_For greeting card product': productTitle,
        },
      },
    ],
  };
}

function getAddonMessage(properties) {
  return String(properties?.['_Greeting card message'] || properties?.['Greeting Card Message'] || '');
}
function getAddonProductTitle(properties) {
  return String(properties?.['_For greeting card product'] || properties?.['For Greeting Card'] || '');
}
function getAddonCardChoice(properties) {
  return String(properties?.['_Message for card'] || '');
}

function getLegacySyncPlan(items, changedItem, requested) {
  const changedProperties = changedItem.properties || {};
  const isAddon = String(changedProperties['_Greeting card message add-on'] || '') === 'true';

  if (isAddon) {
    const linkedVariant = String(changedProperties['_Linked greeting card variant'] || '');
    const linkedMessage = getAddonMessage(changedProperties);
    const linkedTitle = getAddonProductTitle(changedProperties);
    const linkedCardChoice = getAddonCardChoice(changedProperties);
    const mainItem = items.find((item) => {
      const properties = item.properties || {};
      const titleMatches = !linkedTitle || item.product_title === linkedTitle;
      const choiceMatches = !linkedCardChoice || !properties['Message for Card'] || properties['Message for Card'] === linkedCardChoice;
      return item.key !== changedItem.key && String(item.variant_id) === linkedVariant &&
        properties['Greeting Card Message'] === linkedMessage && titleMatches && choiceMatches;
    });
    if (!mainItem || requested >= Math.max(1, Number(mainItem.quantity) || 1)) return null;
    const mainProperties = { ...(mainItem.properties || {}) };
    mainProperties['Message for Card'] = null;
    mainProperties['Greeting Card Message'] = null;
    mainProperties['Greeting Card Message Add-on'] = null;
    return {
      mode: 'addon-change',
      preMutation: { id: mainItem.key, quantity: toQuantity(mainItem.quantity), properties: mainProperties },
      changedKey: changedItem.key,
      changedQuantity: 0,
      warning: 'The greeting card message was removed because the ₹50 message add-on was removed.',
    };
  }

  const message = String(changedProperties['Greeting Card Message'] || '');
  const cardChoice = String(changedProperties['Message for Card'] || '');
  const hasPaidMessage = message && changedProperties['Greeting Card Message Add-on'] != null;
  if (!hasPaidMessage || requested === Number(changedItem.quantity)) return null;
  const addonItem = items.find((item) => {
    const properties = item.properties || {};
    const choiceMatches = !cardChoice || !getAddonCardChoice(properties) || getAddonCardChoice(properties) === cardChoice;
    return String(properties['_Greeting card message add-on'] || '') === 'true' &&
      String(properties['_Linked greeting card variant'] || '') === String(changedItem.variant_id) &&
      getAddonMessage(properties) === message && choiceMatches;
  });
  if (!addonItem) return null;
  return {
    mode: 'main-change',
    preMutation: { id: addonItem.key, quantity: requested },
    changedKey: changedItem.key,
    changedQuantity: requested,
  };
}

function getScopedRemovalWarning(items, changedItem, mainItem, groupId, messageCount) {
  const linkedTitle = String(mainItem?.product_title || changedItem?.properties?.['_For greeting card product'] || 'this greeting card').trim();
  const base = messageCount === 1
    ? `The greeting card message for “${linkedTitle}” was removed.`
    : `${messageCount} greeting card messages for “${linkedTitle}” were removed.`;
  const otherMessagesRemain = items.some((item) => {
    if (item.key === changedItem.key || item.key === mainItem?.key) return false;
    const properties = item.properties || {};
    const otherGroupId = String(properties['_Greeting message group'] || '');
    if (otherGroupId) return otherGroupId !== groupId;
    return Boolean(properties['Greeting Card Message'] || properties['Greeting Card Message 1']);
  });
  return otherMessagesRemain ? `${base} Other greeting card messages in your cart were kept.` : base;
}

/** Build cart synchronization for grouped v4 messages and legacy v3 lines. */
export function getGreetingCardMessageSyncPlan(cart, line, requestedQuantity) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const changedItem = items[line - 1];
  if (!changedItem) return null;
  const properties = changedItem.properties || {};
  const requested = Math.max(0, Number.parseInt(String(requestedQuantity), 10) || 0);
  const groupId = String(properties['_Greeting message group'] || '');
  const isAddon = String(properties['_Greeting card message add-on'] || '') === 'true';

  if (!groupId) return getLegacySyncPlan(items, changedItem, requested);

  if (isAddon) {
    const linkedVariant = String(properties['_Linked greeting card variant'] || '');
    const mainItem = items.find((item) => item.key !== changedItem.key &&
      String(item.variant_id) === linkedVariant &&
      String(item.properties?.['_Greeting message group'] || '') === groupId);
    if (!mainItem) return null;
    const messageCount = Math.max(1, Number.parseInt(String(properties['_Greeting message count'] || mainItem.properties?.['_Greeting message count'] || 1), 10) || 1);
    const expectedQuantity = toQuantity(mainItem.quantity) * messageCount;
    if (requested === expectedQuantity) return null;

    if (requested === 0) {
      return {
        mode: 'addon-change',
        preMutation: {
          id: mainItem.key,
          quantity: toQuantity(mainItem.quantity),
          properties: clearedGreetingProperties(mainItem.properties),
        },
        changedKey: changedItem.key,
        changedQuantity: 0,
        warning: getScopedRemovalWarning(items, changedItem, mainItem, groupId, messageCount),
      };
    }

    return {
      mode: 'addon-change',
      preMutation: null,
      changedKey: changedItem.key,
      changedQuantity: expectedQuantity,
      warning: 'To change individual card messages, remove the message option and add the bundle again. The required message charge was restored.',
    };
  }

  const messageCount = Math.max(0, Number.parseInt(String(properties['_Greeting message count'] || 0), 10) || 0);
  if (!messageCount || requested === Number(changedItem.quantity)) return null;
  const addonItem = items.find((item) => String(item.properties?.['_Greeting card message add-on'] || '') === 'true' &&
    String(item.properties?.['_Greeting message group'] || '') === groupId &&
    String(item.properties?.['_Linked greeting card variant'] || '') === String(changedItem.variant_id));
  if (!addonItem) return null;
  return {
    mode: 'main-change',
    preMutation: { id: addonItem.key, quantity: requested * messageCount },
    changedKey: changedItem.key,
    changedQuantity: requested,
  };
}

if (typeof window !== 'undefined') {
  window.SunlitGreetingCardMessageCoreV5 = {
    buildGreetingCardMessagesPayload,
    getGreetingCardMessageSyncPlan,
    validateGreetingCardMessage,
  };
}

export { MAX_GREETING_CARD_MESSAGE_LENGTH, MAX_GREETING_CARD_MESSAGES };
